import { useState, useMemo, useCallback, memo } from 'react';
import {
  Users,
  CreditCard,
  DollarSign,
  TrendingUp,
  UserPlus,
  Settings,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { StatCard } from './stat-card';
import {
  PeriodSelector,
  OnlineUsersWidget,
  GeographyChart,
  TopCoursesTable,
  RevenueAreaChart,
  BannersWidget,
  FunnelWidget,
  CohortRetentionWidget,
  MrrChurnWidget,
  TierDistributionWidget,
  ActiveHeatmapWidget,
  TopPagesWidget,
  ExpiringSubscriptionsWidget,
  type PeriodValue,
} from './components';
import { useDashboardData, useAnalyticsCurrency, updateAnalyticsCurrency } from '@/hooks/useDashboardAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { notify } from '@/lib/notify';

interface DashboardHeaderProps {
  period: PeriodValue;
  onPeriodChange: (value: PeriodValue) => void;
  baseCurrency: string;
  availableCurrencies: string[];
  onCurrencyChange: (currency: string) => void;
}

const DashboardHeader = memo(function DashboardHeader({
  period,
  onPeriodChange,
  baseCurrency,
  availableCurrencies,
  onCurrencyChange,
}: DashboardHeaderProps) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Аналитика</h1>
        <p className="text-muted-foreground">Обзор ключевых метрик платформы</p>
      </div>
      <div className="flex items-center gap-2">
        <PeriodSelector value={period} onChange={onPeriodChange} />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon">
              <Settings className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Валюта аналитики
            </p>
            {availableCurrencies.map((currency) => (
              <DropdownMenuItem
                key={currency}
                onClick={() => onCurrencyChange(currency)}
                className={baseCurrency === currency ? 'bg-accent' : ''}
              >
                {currency}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
});

export function NewDashboard() {
  const [period, setPeriod] = useState<PeriodValue>({ type: 'month' });

  // Загрузка данных
  const { data: dashboardData, isLoading: isDashboardLoading } = useDashboardData(
    period.type,
    period.startDate,
    period.endDate
  );
  const { data: currencyData, refetch: refetchCurrency } = useAnalyticsCurrency();
  const queryClient = useQueryClient();

  const baseCurrency = currencyData?.currency ?? 'USD';
  const availableCurrencies = currencyData?.availableCurrencies ?? ['USD', 'UZS', 'RUB', 'EUR'];

  const handleCurrencyChange = useCallback(async (currency: string) => {
    try {
      await updateAnalyticsCurrency(currency);
      await refetchCurrency();
      // Инвалидируем все запросы аналитики — бэкенд пересчитает в новой валюте
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      await queryClient.invalidateQueries({ queryKey: ['top-courses'] });
      await queryClient.invalidateQueries({ queryKey: ['confirmed-revenue'] });
      notify.success(`Валюта аналитики изменена на ${currency}`);
    } catch (_error) {
      notify.error('Ошибка изменения валюты');
    }
  }, [refetchCurrency, queryClient]);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: baseCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  }, [baseCurrency]);

  const summary = dashboardData?.summary;
  const revenueData = dashboardData?.charts?.revenue ?? [];

  // Расчет трендов
  const revenueTrend = useMemo(() => {
    if (!summary) {return undefined;}
    if (summary.revenueChange === 0) {return undefined;}
    return {
      value: Math.abs(summary.revenueChange),
      isPositive: summary.revenueChange > 0,
    };
  }, [summary]);

  return (
    <div className="space-y-6 pt-6">
      {/* Header — мемоизирован, не ререндерится при обновлении данных */}
      <DashboardHeader
        period={period}
        onPeriodChange={setPeriod}
        baseCurrency={baseCurrency}
        availableCurrencies={availableCurrencies}
        onCurrencyChange={handleCurrencyChange}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {isDashboardLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[120px]" />
          ))
        ) : (
          <>
            <StatCard
              title="Всего пользователей"
              value={(summary?.totalUsers ?? 0).toLocaleString('ru-RU')}
              icon={<Users className="h-5 w-5" />}
            />
            <StatCard
              title="Новые за период"
              value={(summary?.newUsersInPeriod ?? 0).toLocaleString('ru-RU')}
              icon={<UserPlus className="h-5 w-5" />}
            />
            <StatCard
              title="Активные подписки"
              value={(summary?.activeSubscriptions ?? 0).toLocaleString('ru-RU')}
              icon={<CreditCard className="h-5 w-5" />}
            />
            <StatCard
              title="Онлайн"
              value={(summary?.onlineUsersCount ?? 0).toLocaleString('ru-RU')}
              icon={<TrendingUp className="h-5 w-5" />}
            />
            <StatCard
              title="Выручка"
              value={formatCurrency(summary?.confirmedRevenue ?? 0)}
              icon={<DollarSign className="h-5 w-5" />}
              trend={revenueTrend}
              description={`В ${baseCurrency}`}
            />
          </>
        )}
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isDashboardLoading ? (
          <>
            <Skeleton className="h-[300px] lg:col-span-2" />
            <Skeleton className="h-[300px]" />
          </>
        ) : (
          <>
            <RevenueAreaChart
              data={revenueData}
              baseCurrency={baseCurrency}
              className="lg:col-span-2"
            />
            <OnlineUsersWidget limit={10} />
          </>
        )}
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {isDashboardLoading ? (
          <>
            <Skeleton className="h-[300px]" />
            <Skeleton className="h-[300px]" />
            <Skeleton className="h-[300px]" />
          </>
        ) : (
          <>
            <GeographyChart days={30} />
            <TopCoursesTable period={period} limit={5} />
            <BannersWidget />
          </>
        )}
      </div>

      {/* Charts Row 3: Funnel + MRR/Churn */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <FunnelWidget period={period} className="lg:col-span-2" />
        <MrrChurnWidget months={12} />
      </div>

      {/* Charts Row 4: Cohort Retention + Tier Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CohortRetentionWidget months={12} className="lg:col-span-2" />
        <TierDistributionWidget />
      </div>

      {/* Charts Row 5: Active Heatmap + Top Pages */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ActiveHeatmapWidget days={7} className="lg:col-span-2" />
        <TopPagesWidget days={30} limit={10} />
      </div>

      {/* Charts Row 6: Expiring Subscriptions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ExpiringSubscriptionsWidget className="lg:col-span-1" />
      </div>
    </div>
  );
}
